import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';

@Component({
  selector: 'app-add-conclusion-volume-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './add-conclusion-volume-dialog.component.html',
  styleUrls: ['./add-conclusion-volume-dialog.component.css']
})
export class AddConclusionVolumeDialogComponent implements OnInit {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<AddConclusionVolumeDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});
  meatTypes = signal<any[]>([]);

  applicantName = '';
  quotaYear: number | null = new Date().getFullYear();
  selectedMeatType: any = null;
  distributedVolume: number | null = null;
  deliveredVolume: number | null = null;
  organizationInn = '';
  personIdnp = '';

  ngOnInit(): void {
    this.loadMeatTypes();
  }

  loadMeatTypes(): void {
    this.quotaService.getMeatTypes().subscribe({
      next: (response) => {
        if (response.success && response.res && response.res.content) {
          this.meatTypes.set(response.res.content);
        }
      },
      error: (err) => {
        console.error('Error loading meat types:', err);
      }
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '').slice(0, 14);
    input.value = filtered;
    this[field] = filtered;
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  isFormValid(): boolean {
    return !!(
      this.applicantName.trim() &&
      this.quotaYear &&
      this.selectedMeatType &&
      this.distributedVolume &&
      this.distributedVolume > 0 &&
      this.deliveredVolume &&
      this.deliveredVolume > 0 &&
      this.organizationInn.length === 14 &&
      this.personIdnp.length === 14
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');
    this.fieldErrors.set({});

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: this.translate.instant('addConclusionVolumeDialog.signatureTitle')
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.createVolume(result.token);
      } else if (result?.fieldErrors) {
        this.fieldErrors.set(result.fieldErrors);
      }
    });
  }

  private createVolume(token: string): void {
    this.loading.set(true);

    // Конвертируем тонны в килограммы
    const distributedVolumeInKg = this.distributedVolume! * 1000;
    const deliveredVolumeInKg = this.deliveredVolume! * 1000;

    this.quotaService.createConclusionVolume({
      applicantName: this.applicantName,
      quotaYear: this.quotaYear!,
      meatType: this.selectedMeatType,
      distributedVolume: distributedVolumeInKg,
      deliveredVolume: deliveredVolumeInKg,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      id: null,
      remainingVolume: null,
      status: 'IN_PROGRESS',
      token: token
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open(this.translate.instant('addConclusionVolumeDialog.success'), 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error) => {
        console.error('Error creating conclusion volume:', error);
        this.handleApiErrors(error.error || {});
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    if (Array.isArray(response.res) && response.res.length > 0) {
      const errors: Record<string, string> = {};
      response.res.forEach(err => errors[err.field] = err.ru);
      this.fieldErrors.set(errors);
    } else if (response.msg) {
      this.errorMessage.set(response.msg);
    } else {
      this.errorMessage.set(this.translate.instant('addConclusionVolumeDialog.error'));
    }
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
